/**
 * Вкладка «Операции» дашборда склада: форматирование и раскладка ответа
 * `GET /warehouse-dashboard/operations` на экран. Показатели здесь НЕ
 * пересчитываются (ни дельт, ни сумм) — только подпись и группировка.
 */
import {
  operationsGroupTitle,
  type OperationsMetric,
  type OperationsWeek,
} from "@/api/warehouse_dashboard";
import { fmtDate, ruNum } from "@/lib/format";

const ruInt = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 });
const ruHours = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 1 });

/** Прочерк на месте `null`: показатель за неделю не определён (медиана
 *  пустого, доля от нуля). Не ноль — ноль рисуется числом. */
export const NO_VALUE = "—";
export const NO_VALUE_HINT = "нет данных за неделю";

/**
 * Значение показателя по его `unit` — ОДНА функция на всю вкладку:
 * `count`/`units`/`pallets` — целые с разделителем тысяч, `hours` — `1,5 ч`,
 * `share` (0..1) — `87 %`, `rub` — `1 234 ₽`, `null` — `—`. Незнакомая единица
 * с бэкенда показывается числом С её ключом (`12 xyz`): видно, что вёрстка
 * отстала, и число не выдаётся за штуки.
 */
export function fmtOperationValue(value: number | null, unit: string): string {
  if (value === null) return NO_VALUE;
  switch (unit) {
    case "count":
    case "units":
    case "pallets":
      return ruInt.format(value);
    case "hours":
      return `${ruHours.format(value)} ч`;
    case "share":
      return `${ruInt.format(value * 100)} %`;
    case "rub":
      return `${ruInt.format(value)} ₽`;
    default:
      return `${ruNum.format(value)} ${unit}`;
  }
}

export interface MetricGroup {
  group: string;
  title: string;
  metrics: OperationsMetric[];
}

/** Показатели по группам в порядке ответа: группа — по первому появлению,
 *  внутри — как пришли. Порядок задаёт бэкенд, фронт не сортирует. */
export function groupMetrics(metrics: OperationsMetric[]): MetricGroup[] {
  const out: MetricGroup[] = [];
  const byKey = new Map<string, MetricGroup>();
  for (const m of metrics) {
    let g = byKey.get(m.group);
    if (!g) {
      g = { group: m.group, title: operationsGroupTitle(m.group), metrics: [] };
      byKey.set(m.group, g);
      out.push(g);
    }
    g.metrics.push(m);
  }
  return out;
}

/** Номер ISO-недели (пн–вс, неделя 1 — та, где первый четверг года) по
 *  `YYYY-MM-DD` начала. Считается в UTC-арифметике от разобранных чисел —
 *  зона браузера не участвует. Неразборчивое — `null`. */
export function isoWeekNumber(iso: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(t)) return null;
  const d = new Date(t);
  const dow = d.getUTCDay() || 7; // пн=1 … вс=7
  d.setUTCDate(d.getUTCDate() + 4 - dow); // четверг той же недели решает год
  const yearStart = Date.UTC(d.getUTCFullYear(), 0, 1);
  return Math.ceil(((d.getTime() - yearStart) / 86_400_000 + 1) / 7);
}

/** Подпись недели — ОДНА на дашборд: `нед. 38` (у текущей — `нед. 39 · тек.`)
 *  и диапазон `14.09–20.09`. Шапка «Операций», строка-период и подсказки
 *  столбцов «Обзора» берут её отсюда. */
export function weekCaption(week: OperationsWeek): { title: string; range: string } {
  const n = isoWeekNumber(week.start);
  const a = fmtDate(week.start);
  const b = fmtDate(week.end);
  const base = n === null ? `нед. ${week.start}` : `нед. ${n}`;
  return {
    title: week.partial ? `${base} · тек.` : base,
    range: a && b ? `${a.slice(0, 5)}–${b.slice(0, 5)}` : `${week.start}–${week.end}`,
  };
}

/** Полный диапазон недели для подсказки: `21.09–27.09.2026`. */
export function fmtWeekRange(start: string, end: string): string {
  const a = fmtDate(start);
  const b = fmtDate(end);
  return a && b ? `${a.slice(0, 5)}–${b}` : `${start}–${end}`;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Возраст самого старого в очереди относительно `as_of` ОТВЕТА (не часов
 * браузера — картинка и её «сейчас» из одного расчёта): до часа — минуты, до
 * двух суток — часы, дальше — дни. Неразборчивая дата — как есть (это видно).
 */
export function fmtAge(oldestAt: string, asOf: string): string {
  const from = new Date(oldestAt).getTime();
  const to = new Date(asOf).getTime();
  if (Number.isNaN(from) || Number.isNaN(to)) return oldestAt;
  const ms = Math.max(0, to - from);
  if (ms < HOUR) return `${Math.floor(ms / MINUTE)} мин`;
  if (ms < 2 * DAY) return `${Math.floor(ms / HOUR)} ч`;
  return `${Math.floor(ms / DAY)} дн`;
}

export interface SparkPoint {
  x: number;
  y: number;
}

/**
 * Точки спарклайна: `null` → `null` (разрыв линии, а не ноль). Шкала от нуля
 * (все показатели неотрицательны): иначе колебание 100 ↔ 102 рисовалось бы
 * падением до дна. Все нули — линия по дну.
 */
export function sparkPoints(
  values: (number | null)[],
  width: number,
  height: number,
  pad = 2,
): (SparkPoint | null)[] {
  const nums = values.filter((v): v is number => v !== null);
  const lo = Math.min(0, ...nums);
  const hi = Math.max(0, ...nums);
  const step = values.length > 1 ? (width - 2 * pad) / (values.length - 1) : 0;
  return values.map((v, i) => {
    if (v === null) return null;
    const x = pad + i * step;
    const y = hi === lo ? height - pad : height - pad - ((v - lo) / (hi - lo)) * (height - 2 * pad);
    return { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 };
  });
}
